/**
 * usePublicPackages.js – AllFlowX Billing Catalog Hook
 *
 * ARCHITEKTUR (Phase 2B.2):
 *   Single Source of Truth: MongoDB billing_packages via Public Packages API
 *
 *   Datenfluss:
 *     MongoDB billing_packages
 *       ↓ /api/public/v1/packages
 *     usePublicPackages() Hook (sessionStorage Cache 5 Min)
 *       ↓
 *     Register.js, PaywallModal.js, AdminSubscription.js
 *
 * KEINE Preise mehr hardkodiert im Frontend.
 * Alle Paketdaten kommen ausschließlich von dieser API.
 *
 * FALLBACK:
 *   Wenn API nicht erreichbar → sichere Fallback-Werte.
 *   Dies verhindert leere Seiten bei kurzzeitigen Verbindungsproblemen.
 *   Fallback-Werte müssen bei Preisänderungen NICHT angepasst werden –
 *   sie dienen nur als Notfall-Anzeige.
 */

import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'afx_v2_packages_2b5';
const CACHE_TTL = 5 * 60 * 1000; // 5 Minuten

/**
 * Fallback-Werte wenn API nicht erreichbar.
 * NUR für Notfälle. Nicht für normale Nutzung.
 * Wird nie angezeigt wenn API erreichbar.
 */
const FALLBACK_PACKAGES = {
  starter: {
    slug: 'starter',
    name: 'Starter',
    price: { price_net_eur: 29.90, price_net_cents: 2990, currency: 'EUR', interval: 'month' },
    limits: { employees_per_location: 15, extra_employee_addons: [
      { slug: 'employees_plus_5', label: '+5 Mitarbeiter', employees: 5, monthly_cents: 599 },
      { slug: 'employees_plus_10', label: '+10 Mitarbeiter', employees: 10, monthly_cents: 999 },
      { slug: 'employees_plus_20', label: '+20 Mitarbeiter', employees: 20, monthly_cents: 1799 },
    ]},
    features_public: ['Aufgaben', 'MHD-Kontrolle', 'Temperaturkontrolle'],
    modules_included: [],
    industry_tags: ['all'],
    is_bookable: true,
    _is_fallback: true,
  },
  business: {
    slug: 'business',
    name: 'Business',
    price: { price_net_eur: 39.90, price_net_cents: 3990, currency: 'EUR', interval: 'month' },
    limits: { employees_per_location: 15 },
    features_public: [],
    modules_included: [],
    industry_tags: ['all'],
    is_bookable: true,
    _is_fallback: true,
  },
  professional_ai: {
    slug: 'professional_ai',
    name: 'Professional AI',
    price: { price_net_eur: 59.90, price_net_cents: 5990, currency: 'EUR', interval: 'month' },
    limits: { employees_per_location: 15 },
    features_public: [],
    modules_included: [],
    industry_tags: ['all'],
    is_bookable: true,
    _is_fallback: true,
  },
};

/**
 * usePublicPackages – Haupthook
 *
 * @returns {Object} {
 *   packages:          { [slug]: PackageData } – alle Pakete als Map
 *   starter:           PackageData | null
 *   business:          PackageData | null
 *   professional_ai:   PackageData | null
 *   loading:           boolean
 *   error:             Error | null
 *   isFallback:        boolean – True wenn Fallback-Daten verwendet werden
 *   getPrice:          (slug) => number | null   – Nettopreis in €
 *   getGrossPrice:     (slug) => string | null   – Bruttopreis (+ 19% MwSt.) als "XX.XX"
 *   getLimits:         (slug) => Object          – limits Objekt
 *   getModules:        (slug) => string[]        – Liste der Module
 *   getAddons:         (slug) => AddonArray      – Mitarbeiter-Add-ons
 *   getFeatures:       (slug) => string[]        – Öffentliche Feature-Liste
 *   invalidateCache:   () => void                – Cache manuell leeren (für Tests)
 * }
 */
const usePublicPackages = () => {
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFallback, setIsFallback] = useState(false);

  const invalidateCache = () => {
    try {
      sessionStorage.removeItem(CACHE_KEY);
    } catch {}
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // 1. sessionStorage Cache prüfen
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw) {
          const { data, ts } = JSON.parse(raw);
          if (Date.now() - ts < CACHE_TTL) {
            if (!cancelled) {
              setPackages(data);
              setIsFallback(false);
              setLoading(false);
            }
            return;
          }
        }
      } catch {}

      // 2. API abrufen
      try {
        const res = await fetch(`${API}/api/public/v1/packages`);
        if (!res.ok) throw new Error(`API Fehler: ${res.status}`);
        const response = await res.json();

        // Als Slug-Map strukturieren: { starter: {...}, business: {...}, ... }
        const pkgMap = {};
        (response.packages || []).forEach(p => {
          pkgMap[p.slug] = p;
        });

        // Cache speichern
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: pkgMap, ts: Date.now() }));
        } catch {}

        if (!cancelled) {
          setPackages(pkgMap);
          setIsFallback(false);
          setLoading(false);
        }
      } catch (err) {
        console.warn('[usePublicPackages] API nicht erreichbar, verwende Fallback:', err.message);
        if (!cancelled) {
          setPackages(FALLBACK_PACKAGES);
          setIsFallback(true);
          setError(err);
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  // ── Helper-Funktionen ─────────────────────────────────────────────────────

  /** Nettopreis in € für ein Paket (z.B. 29.90) */
  const getPrice = (slug) => packages?.[slug]?.price?.price_net_eur ?? null;

  /** Bruttopreis als formatierter String (z.B. "35.58") */
  const getGrossPrice = (slug) => {
    const net = getPrice(slug);
    if (net === null) return null;
    return (net * 1.19).toFixed(2);
  };

  /** Netto-Centbetrag (z.B. 2990) */
  const getPriceCents = (slug) => packages?.[slug]?.price?.price_net_cents ?? null;

  /** limits-Objekt { employees_per_location, extra_employee_addons, ... } */
  const getLimits = (slug) => packages?.[slug]?.limits ?? {};

  /** Inkludierte Mitarbeiter pro Standort */
  const getEmployeesPerLocation = (slug) => getLimits(slug).employees_per_location ?? 15;

  /** Liste der canonical Module-Keys */
  const getModules = (slug) => packages?.[slug]?.modules_included ?? [];

  /** Mitarbeiter-Add-ons Array */
  const getAddons = (slug) => getLimits(slug).extra_employee_addons ?? [];

  /** Öffentliche Feature-Liste (für Landingpage) */
  const getFeatures = (slug) => packages?.[slug]?.features_public ?? [];

  /** Industry Tags */
  const getIndustryTags = (slug) => packages?.[slug]?.industry_tags ?? ['all'];

  return {
    packages: packages ?? {},
    loading,
    error,
    isFallback,
    // Direktzugriff auf Kern-Pakete
    starter:          packages?.starter ?? null,
    business:         packages?.business ?? null,
    professional_ai:  packages?.professional_ai ?? null,
    // Helper-Funktionen
    getPrice,
    getGrossPrice,
    getPriceCents,
    getLimits,
    getEmployeesPerLocation,
    getModules,
    getAddons,
    getFeatures,
    getIndustryTags,
    invalidateCache,
  };
};

export default usePublicPackages;
